
import React, { useRef, useState } from "react";
import { Form, FormInstance } from "antd";
import { useMercuryContext } from "../../context";
import { successfulNotification, failedNotification } from "../../common/utils/notifications";
import NewClientApi from "./NewClientApi";
import EditClientApi from "./EditClientApi";
import NewApiClientConfirmation from "./NewApiClientConfirmation";
import { ApiClient, ApiClientData, Permission, EditingValue, Application } from "./types/apiClients";
import { handleApiError } from "common/utils/handleApiError";

const defaultNewClient = { isActive: false, data: null };

const ApiClientRequest: React.FC<{
    initialValues?: Partial<ApiClient>;
    refresh: () => void;
    applicationsList?: Application[];
    dashboardsList?: any[];
    permissionsList?: Permission[];
    mode?: "add" | "edit";
    buttonLabel?: string;
    disabled?: boolean;
    hasPermissions?: boolean;
}> = ({
    initialValues,
    refresh,
    applicationsList = [],
    dashboardsList = [],
    permissionsList = [],
    mode = "add",
}) => {
        const [form] = Form.useForm();
        const { umsClient } = useMercuryContext();
        const [isOpen, setIsOpen] = useState(false);
        const [addedNewClient, setAddedNewClient] = useState<{ isActive: boolean; data: ApiClientData | null }>(defaultNewClient);
        const [editing, setEditing] = useState<EditingValue>({ isEditing: false, editingRow: null });
        const editFormRef = useRef<FormInstance>(null)
        const openDrawer = () => {
            setIsOpen(true);
            form.resetFields();
            if (initialValues && mode === "edit") {
                form.setFieldsValue({
                    nameValue: initialValues.name,
                    descriptionValue: initialValues.description,
                    isActiveValue: initialValues.isActive,
                    customerApplicationsValue: [initialValues.customerApplicationName],
                });
                setEditing({
                    isEditing: false, editingRow:
                    {
                        _id: initialValues.id || "",
                        name: initialValues.name || "",
                        description: initialValues.description || "",
                        customerApplication: initialValues.customerApplicationName || "",
                        customerApplicationId: initialValues.customerApplication || "",
                        permissions: initialValues.permissions || [],
                        isActive: initialValues.isActive ?? false,
                    },
                });
            }
        };

        const closeDrawer = () => {
            setIsOpen(false);
            form.resetFields();
            setEditing({ isEditing: false, editingRow: null });
        };

        const handleFinishNewClient = async (values: any, permissions: any[]) => {
            try {
                const payload = {
                    name: values.nameValue,
                    description: values.descriptionValue,
                    customerApplication: values.customerApplicationValue,
                    isActive: values.isActiveValue ?? false,
                    permissions,
                };
                const res = await umsClient.post(`/ums-v1/api-clients`, payload);
                setAddedNewClient({ isActive: true, data: res.data });
                successfulNotification("Successfully added", `Client Api (${res.data.name}) was created.`);
                closeDrawer();
                refresh();
            } catch (error: any) {
                handleApiError(error, "Failed to add new Api clients.");
            }
        };

        const handleFinishEditing = async (vals: any) => {
            try {
                const permissions: any[] = [];
                for (const [key, value] of Object.entries(vals)) {
                    if (key.includes("permissions")) {
                        const id = key.split("-").pop();
                        const permKey = key.split("~").pop()?.replace(`-${id}`, "");
                        const found = permissions.find((p) => p.permissionId === id);
                        if (found) {
                            found[permKey!] = !!value;
                        } else {
                            const newPermission: any = { permissionId: id };
                            newPermission[permKey!] = !!value;
                            permissions.push(newPermission);
                        }
                    }
                }
                const { nameValue, descriptionValue, customerApplicationsValue, isActiveValue, _id } = vals;
                const patchBody: any = {
                    name: nameValue,
                    description: descriptionValue,
                    isActive: isActiveValue,
                    permissions,
                };
                if (customerApplicationsValue) {
                    patchBody.customerApplication = Array.isArray(customerApplicationsValue)
                        ? customerApplicationsValue[0]
                        : customerApplicationsValue;
                }

                const res = await umsClient.patch(`/ums-v1/api-clients/${_id}`, patchBody);
                successfulNotification("Successfully updated", `Client Api (${res.data.name}) was updated.`);
                closeDrawer();
                refresh();
            } catch (error: any) {
                handleApiError(error, "Failed to update Api clients.");
            }
        };

        return (
            <>
                {mode === "add" ? (
                    <NewClientApi
                        visible={isOpen}
                        form={form as FormInstance}
                        permissionsList={permissionsList}
                        applicationsList={applicationsList}
                        hasPermissions
                        onOpen={openDrawer}
                        onClose={closeDrawer}
                        onRefresh={refresh}
                        onFinish={handleFinishNewClient}
                    />
                ) : (
                    <EditClientApi
                        visible={isOpen}
                        form={form as FormInstance}
                        editFormRef={editFormRef}
                        permissionsList={permissionsList}
                        applicationsList={applicationsList}
                        editing={editing}
                        closeEditDrawer={closeDrawer}
                        onFinishEditing={handleFinishEditing}
                        onOpen={openDrawer}
                        hasPermissions
                    />
                )}
                <NewApiClientConfirmation
                    addedNewClient={addedNewClient}
                    defaultNewClient={defaultNewClient}
                    setAddedNewClient={setAddedNewClient}
                />
            </>
        );
    };

export default ApiClientRequest;
